import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';

/**
 * Trust-building "how it works" section. Three steps — Choose · Contact ·
 * Book — designed to reassure first-time visitors that hiring a tradesman
 * through AfroEspace is straightforward and safe. Replaces the European
 * inspiration mosaic on the Senegal home page, where service browsing
 * (rather than aspirational beauty/event imagery) is what visitors need.
 *
 * Layout: three equal columns on md+ with a connector line between them;
 * stacked on mobile with the same numbered step badges.
 */
function Step({
  index,
  Icon,
  title,
  description,
}: {
  index: number;
  Icon: SvgIconComponent;
  title: string;
  description: string;
}) {
  return (
    <Stack alignItems="center" textAlign="center" sx={{ position: 'relative', px: 2 }}>
      {/* Numbered badge sits at the top so the visual rhythm reads
          1 → 2 → 3 even when the cards stack vertically on mobile. */}
      <Box
        sx={{
          position: 'relative',
          mb: 2.5,
          width: 96,
          height: 96,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 12px 28px rgba(0,0,0,0.5)'
              : '0 12px 28px rgba(200,120,43,0.25)',
          color: 'common.white',
        }}
      >
        <Icon sx={{ fontSize: '2.5rem' }} />
        {/* Small numeric chip in the corner of the badge — quick visual
            anchor for the step order. */}
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'primary.main',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '0.95rem',
          }}
        >
          {index}
        </Box>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 280 }}>
        {description}
      </Typography>
    </Stack>
  );
}

export function HowItWorksSection() {
  const { t } = useTranslation();

  const steps: { Icon: SvgIconComponent; title: string; description: string }[] = [
    {
      Icon: SearchRoundedIcon,
      title: t('home.step1Title'),
      description: t('home.step1Desc'),
    },
    {
      Icon: ChatRoundedIcon,
      title: t('home.step2Title'),
      description: t('home.step2Desc'),
    },
    {
      Icon: EventAvailableRoundedIcon,
      title: t('home.step3Title'),
      description: t('home.step3Desc'),
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.paper', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="overline"
            color="primary.main"
            sx={{ letterSpacing: '0.18em', fontWeight: 700 }}
          >
            {t('home.howItWorksLabel')}
          </Typography>
          <Typography variant="h3" sx={{ mt: 1, mb: 1 }}>
            {t('home.howItWorksTitle')}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ fontSize: '1.0625rem', maxWidth: 620, mx: 'auto' }}
          >
            {t('home.howItWorksHint')}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 4, md: 3 }} alignItems="flex-start">
          {steps.map((step, i) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Step
                index={i + 1}
                Icon={step.Icon}
                title={step.title}
                description={step.description}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
